import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Heart, Share2, Music, Image, Video, Hash, TrendingUp, ArrowLeft } from 'lucide-react';
import { Post } from './Post';
import { DiscoverPanel } from './DiscoverPanel';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Post as PostType, createPost, deletePost, getPosts, subscribeToPosts } from '../../lib/posts';
import { RealtimeChannel } from '@supabase/supabase-js';

interface FormattedPost {
  id: string;
  author: {
    id: string;
    username: string;
    image?: string;
    badge?: string;
  };
  content: string;
  audio?: {
    url: string;
    title: string;
    duration: string;
  };
  image?: string;
  created_at: string;
  likes: number;
  comments: number;
}

export function Feed() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [posts, setPosts] = useState<FormattedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newPost, setNewPost] = useState('');
  const subscriptionRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    loadPosts();

    // Set up real-time subscription
    subscriptionRef.current = subscribeToPosts((post, eventType) => {
      if (eventType === 'INSERT') {
        const formattedPost = formatPost(post);
        setPosts(prev => [formattedPost, ...prev]);
      } else if (eventType === 'DELETE') {
        setPosts(prev => prev.filter(p => p.id !== post.id));
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  const formatPost = (post: PostType): FormattedPost => ({
    id: post.id,
    author: {
      id: post.user_id,
      username: post.profiles?.username || 'Unbekannter Benutzer',
      avatar_url: post.profiles?.avatar_url,
      badge: post.profiles?.artist_types[0] || 'Künstler',
    },
    content: post.content,
    ...(post.audio_url && {
      audio: {
        url: post.audio_url,
        title: post.audio_title || '',
        duration: post.audio_duration || ''
      }
    }),
    ...(post.image_url && { image: post.image_url }),
    created_at: new Date(post.created_at).toLocaleString('de-DE'),
    likes: post.likes_count,
    comments: post.comments_count
  });

  async function loadPosts() {
    try {
      const postsData = await getPosts();
      if (!postsData) {
        throw new Error('Keine Posts gefunden');
      }
      setPosts(postsData.map(formatPost));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Laden der Posts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPost.trim()) return;

    try {
      const post = await createPost(newPost.trim());
      const formattedPost = formatPost(post);
      setPosts(prev => [formattedPost, ...prev]);
      setNewPost('');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Erstellen des Posts';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000); // Clear error after 3 seconds
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      // Optimistically remove the post from the UI
      setPosts(prev => prev.filter(post => post.id !== postId));
      
      // Then delete it from the database
      await deletePost(postId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Löschen des Posts';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000); // Clear error after 3 seconds
      // If deletion fails, reload the posts to restore the correct state
      loadPosts();
    }
  };

  if (loading) {
    return <div className="flex-1 flex items-center justify-center">Laden...</div>;
  }

  return (
    <div className="flex-1 h-screen overflow-hidden">
      <div className="flex flex-col lg:flex-row h-full">
        <div className="flex-1 overflow-y-auto">
          {/* Mobile Header */}
          <div className="md:hidden p-4 border-b border-divider flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-background-secondary rounded-full text-text-primary"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-bold">Feed</h1>
          </div>

          {error && (
            <div className="p-4 text-red-500 bg-red-500/10 border border-red-500/50">
              {error}
            </div>
          )}

          <div className="p-4 border-b border-divider">
            <form onSubmit={handleCreatePost}>
              <div className="flex gap-4">
                <div className="flex-1">
                  <textarea
                    placeholder="Teile deine Musik, Gedanken oder Updates..."
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    className="w-full bg-background-secondary rounded-lg p-3 resize-none focus:outline-none focus:ring-1 focus:ring-accent-primary"
                    rows={3}
                  />
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-3 gap-2">
                    <div className="flex gap-2">
                      <button type="button" className="p-2 hover:bg-background-tertiary rounded-full text-text-secondary hover:text-accent-primary">
                        <Music size={20} />
                      </button>
                      <button type="button" className="p-2 hover:bg-background-tertiary rounded-full text-text-secondary hover:text-accent-primary">
                        <Image size={20} />
                      </button>
                      <button type="button" className="p-2 hover:bg-background-tertiary rounded-full text-text-secondary hover:text-accent-primary">
                        <Video size={20} />
                      </button>
                    </div>
                    <button 
                      type="submit"
                      disabled={!newPost.trim()}
                      className="w-full sm:w-auto px-4 py-2 bg-accent-primary hover:bg-accent-hover rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Posten
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div className="pb-4">
            {posts.map((post) => (
              <Post
                key={post.id}
                id={post.id}
                author={post.author}
                content={post.content}
                audio={post.audio}
                image={post.image}
                timestamp={post.created_at}
                likes={post.likes}
                comments={post.comments}
                onDelete={handleDeletePost}
              />
            ))}
          </div>
        </div>

        <div className="hidden lg:block">
          <DiscoverPanel />
        </div>
      </div>
    </div>
  );
}